import { type showToast } from '@cleanlab/design-system/components'
import { upperFirst } from 'lodash'

import { ENTRY_ID_PARAM, PAGE_TAB_PARAM } from '@/components/Filters/filterConsts'

export const ERROR_DOM_ID = 'error-container'

export const APP_PATHS = {
  DEFAULT: '/projects',
  ACCOUNT: '/account',
  PROJECTS: '/projects',
  PROJECTS_CREATE: '/projects/create',
  PROJECTS_QUERY_LOGS: '/projects/:projectId/logs',
  PROJECTS_ISSUES: '/projects/:projectId/issues',
  PROJECTS_REMEDIATIONS: '/projects/:projectId/remediations',
  PROJECTS_ANALYTICS: '/projects/:projectId/analytics',
  PROJECTS_ALERTS: '/projects/:projectId/alerts',
  PROJECTS_SETTINGS: '/projects/:projectId/settings',
  PROJECTS_EVALUATIONS: '/projects/:projectId/evaluations',
  PROJECTS_GUARDRAILS: '/projects/:projectId/guardrails',
  PROJECTS_CLASSIFICATION: '/projects/:projectId/classification',
  ADMIN: '/admin',
  ADMIN_ORGANIZATIONS: '/admin/organizations',
  ADMIN_ORGANIZATIONS_CREATE: '/admin/organizations/create',
  ADMIN_ORGANIZATIONS_MEMBERS: '/admin/organizations/members',
  ADMIN_ORGANIZATIONS_PROJECTS: '/admin/organizations/projects',
  ADMIN_USERS: '/admin/users',
  ADMIN_CLUSTER_RANKS: '/admin/cluster-ranks',
  ADMIN_PROJECT_CLUSTERING: '/admin/project-clustering',
} as const

export const PAGE_TITLES = {
  ACCOUNT: 'Account',
  PROJECTS: 'Projects',
  PROJECTS_CREATE: 'Create project',
  PROJECTS_QUERY_LOGS: 'Logs',
  PROJECTS_ISSUES: 'Expert Workspace',
  PROJECTS_REMEDIATIONS: 'Remediations',
  PROJECTS_ANALYTICS: 'Analytics',
  PROJECTS_ALERTS: 'Alerts & Notifications',
  PROJECTS_SETTINGS: 'Settings',
  PROJECTS_EVALUATIONS: 'Evaluations',
  PROJECTS_GUARDRAILS: 'Guardrails',
  PROJECTS_CLASSIFICATION: 'Classification',
  ADMIN: 'Admin',
  ADMIN_ORGANIZATIONS: 'Organizations',
  ADMIN_ORGANIZATIONS_CREATE: 'Create organization',
  ADMIN_ORGANIZATIONS_MEMBERS: 'Organization Members',
  ADMIN_ORGANIZATIONS_PROJECTS: 'Organization Projects',
  ADMIN_USERS: 'Users',
  ADMIN_CLUSTER_RANKS: 'Cluster Ranks',
  ADMIN_PROJECT_CLUSTERING: 'Project Clustering',
} as const satisfies Omit<Record<keyof typeof APP_PATHS, string>, 'DEFAULT'>

export const APP_PATH_HELPERS = {
  CREATE_PROJECT_QUERY_LOGS_PATH: (projectId: string, entryId?: string) =>
    `${APP_PATHS.PROJECTS_QUERY_LOGS.replace(':projectId', projectId)}${entryId ? `?${ENTRY_ID_PARAM}=${entryId}` : ''}`,
  CREATE_PROJECT_ISSUES_PATH: (projectId: string, entryId?: string) =>
    `${APP_PATHS.PROJECTS_ISSUES.replace(':projectId', projectId)}${entryId ? `?${ENTRY_ID_PARAM}=${entryId}` : ''}`,
  CREATE_PROJECT_REMEDIATIONS_PATH: (projectId: string, entryId?: string, gridTab?: string) =>
    `${APP_PATHS.PROJECTS_REMEDIATIONS.replace(':projectId', projectId)}${entryId ? `?${ENTRY_ID_PARAM}=${entryId}` : ''}${
      gridTab ? `${entryId ? '&' : '?'}${PAGE_TAB_PARAM}=${gridTab}` : ''
    }`,
  CREATE_PROJECT_ANALYTICS_PATH: (projectId: string) =>
    APP_PATHS.PROJECTS_ANALYTICS.replace(':projectId', projectId),
  CREATE_PROJECT_ALERTS_PATH: (projectId: string) =>
    APP_PATHS.PROJECTS_ALERTS.replace(':projectId', projectId),
  CREATE_PROJECT_SETTINGS_PATH: (projectId: string) =>
    APP_PATHS.PROJECTS_SETTINGS.replace(':projectId', projectId),
  CREATE_PROJECT_GUARDRAILS_PATH: (projectId: string) =>
    APP_PATHS.PROJECTS_GUARDRAILS.replace(':projectId', projectId),
  CREATE_PROJECT_EVALUATIONS_PATH: (projectId: string) =>
    APP_PATHS.PROJECTS_EVALUATIONS.replace(':projectId', projectId),
  CREATE_PROJECT_CLASSIFICATION_PATH: (projectId: string) =>
    APP_PATHS.PROJECTS_CLASSIFICATION.replace(':projectId', projectId),
}

export const AUTO_SAVE_DEBOUNCE_TIME = 2000

export const CODEX_API_BASE_URL = process.env.NEXT_PUBLIC_CODEX_API_BASE_URL
export const CODEX_API_ROUTE = `${CODEX_API_BASE_URL}/api` as const

export const ENTERPRISE_PLAN_ITEMS = [
  'Everything in Codex Core Plan',
  'Custom pricing with volume discounts',
  'VPC and cloud integration',
  'Dedicated support engineer',
  'Priority for new feature requests',
]

export const CLEANLAB_COMMUNITY_SLACK_URL =
  'https://cleanlab-community.slack.com/archives/C03ENS0QQHJ'

export const TOAST_OPTS = {
  duration: 5000,
}

/**
 * Formats an item type label with an optional item name for display in toast messages.
 *
 * @param itemTypeLabel - The type label of the item (e.g., "Project", "Guardrail", "Evaluation")
 * @param itemName - Optional name of the specific item. If provided, will be wrapped in quotes.
 * @returns A formatted string combining the type label and name. If no name is provided, returns just the type label.
 *
 * @example
 * ```ts
 * getItemTypeNamePair("Project", "My Project") // Returns: 'Project "My Project"'
 * getItemTypeNamePair("Guardrail", null) // Returns: 'Guardrail'
 * ```
 */
const getItemTypeNamePair = (itemTypeLabel: string, itemName: string | null | undefined) => {
  return `${itemTypeLabel}${itemName ? ` "${itemName}"` : ''}`
}

type ToastErrorArgs = {
  typeLabel: string
  itemName: string | null | undefined
  errorMessage: string
}

type ToastSuccessArgs = {
  typeLabel: string
  itemName?: string | null | undefined
}

export const TOAST_PROPS: Record<
  'delete' | 'create' | 'update' | 'publish',
  {
    error: (args: ToastErrorArgs) => Parameters<typeof showToast>[0]
    success: (args: ToastSuccessArgs) => Parameters<typeof showToast>[0]
  }
> & {
  operation: {
    error: (args: ToastErrorArgs & { operationPresent: string }) => Parameters<typeof showToast>[0]
    success: (args: ToastSuccessArgs & { operationPast: string }) => Parameters<typeof showToast>[0]
  }
} = {
  delete: {
    success: ({ typeLabel: itemTypeLabel, itemName }) => ({
      heading: `Deleted ${itemTypeLabel}`,
      description: !itemName
        ? undefined
        : `The ${getItemTypeNamePair(itemTypeLabel, itemName)} has been deleted successfully`,
      status: 'success',
    }),
    error: ({ typeLabel: itemTypeLabel, itemName, errorMessage }) => ({
      heading: `Could not delete ${getItemTypeNamePair(itemTypeLabel, itemName)}`,
      description: errorMessage,
      status: 'warning',
    }),
  },
  create: {
    success: ({ typeLabel: itemTypeLabel, itemName }) => ({
      heading: `Created ${itemTypeLabel}`,
      description: !itemName
        ? undefined
        : `The ${getItemTypeNamePair(itemTypeLabel, itemName)} has been created successfully`,
      status: 'success',
    }),
    error: ({ typeLabel: itemTypeLabel, itemName, errorMessage }) => ({
      heading: `Could not create ${getItemTypeNamePair(itemTypeLabel, itemName)}`,
      description: errorMessage,
      status: 'warning',
    }),
  },
  update: {
    success: ({ typeLabel, itemName }) => ({
      heading: `Updated ${typeLabel}`,
      description: !itemName
        ? undefined
        : `The ${getItemTypeNamePair(typeLabel, itemName)} has been updated successfully`,
      status: 'success',
    }),
    error: ({ typeLabel, itemName, errorMessage }) => ({
      heading: `Could not update ${getItemTypeNamePair(typeLabel, itemName)}`,
      description: errorMessage,
      status: 'warning',
    }),
  },
  publish: {
    success: ({ typeLabel, itemName }) => ({
      heading: `Published ${typeLabel}`,
      description: !itemName
        ? undefined
        : `The ${getItemTypeNamePair(typeLabel, itemName)} has been published successfully`,
      status: 'success',
    }),
    error: ({ typeLabel, itemName, errorMessage }) => ({
      heading: `Could not publish ${getItemTypeNamePair(typeLabel, itemName)}`,
      description: errorMessage,
      status: 'warning',
    }),
  },
  operation: {
    success: ({ operationPast, typeLabel, itemName }) => ({
      heading: `${upperFirst(operationPast)} ${typeLabel}`,
      description: !itemName
        ? undefined
        : `The ${getItemTypeNamePair(typeLabel, itemName)} has been ${operationPast} successfully`,
      status: 'success',
    }),
    error: ({ operationPresent, typeLabel, itemName, errorMessage }) => ({
      heading: `Could not ${operationPresent} ${getItemTypeNamePair(typeLabel, itemName)}`,
      description: errorMessage,
      status: 'warning',
    }),
  },
}

export const EN_DASH_CHAR = '\u2013' as const
export const EM_DASH_CHAR = '\u2014' as const

// Use this character to represent empty or missing data in the UI
// \u2014 is the unicode character for an em dash
export const EMPTY_DATA_CHAR = EM_DASH_CHAR
